import { notify } from '../utils/notify';
import React, { useState, useEffect } from 'react';
import { permissionsAPI, userAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Save } from 'lucide-react';
import './Security.css';

const Security = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const availablePermissions = {
    dashboard: ['view_dashboard', 'view_stats'],
    employees: ['view_employees', 'create_employee', 'edit_employee', 'delete_employee'],
    attendance: ['view_attendance', 'manage_attendance', 'approve_attendance'],
    payroll: ['view_payroll', 'create_payroll', 'approve_payroll', 'process_payroll'],
    finance: ['view_finance', 'create_expense', 'approve_expense', 'view_reports'],
    contracts: ['view_contracts', 'create_contract', 'edit_contract', 'activate_contract'],
    settings: ['view_settings', 'edit_settings', 'manage_users', 'manage_permissions']
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      setLoading(true);
      const response = await permissionsAPI.getUserPermissions(userId);
      setPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    loadUserPermissions(user.id);
  };

  const togglePermission = (module, permission) => {
    setPermissions(prev => {
      const modulePerms = prev[module] || [];
      const hasPermission = modulePerms.includes(permission);
      
      return {
        ...prev,
        [module]: hasPermission
          ? modulePerms.filter(p => p !== permission)
          : [...modulePerms, permission]
      };
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      await permissionsAPI.updateUserPermissions(selectedUser.id, permissions);
      notify(t('Permissions updated successfully!'), 'success');
    } catch (error) {
      notify(error.response?.data?.message || t('Error updating permissions'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="security-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Security & Permissions')}</h1>
          <p className="page-sub">{t('Manage user access and permissions')}</p>
        </div>
      </div>

      <div className="sec-layout">
        {/* Users List */}
        <div className="card sec-users">
          <h3 className="sec-users__title">{t('Users')}</h3>
          <div className="sec-users__list">
            {users.map(user => (
              <div
                key={user.id}
                className={`sec-user${selectedUser?.id === user.id ? ' is-active' : ''}`}
                onClick={() => handleUserSelect(user)}
              >
                <div className="sec-user__info">
                  <strong>{user.first_name} {user.last_name}</strong>
                  <p>{user.email}</p>
                  <span className="status status--neutral sec-role">{user.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Permissions Panel */}
        <div className="card sec-panel">
          {!selectedUser ? (
            <div className="empty">
              <p>{t('Select a user to manage permissions')}</p>
            </div>
          ) : (
            <>
              <div className="sec-panel__head">
                <h2>{t('Permissions for {name}', { name: `${selectedUser.first_name} ${selectedUser.last_name}` })}</h2>
                <span className="status status--neutral sec-role">{selectedUser.role}</span>
              </div>

              {loading ? (
                <div className="empty">{t('Loading permissions...')}</div>
              ) : (
                <>
                  <div className="sec-modules">
                    {Object.entries(availablePermissions).map(([module, perms]) => (
                      <div key={module} className="sec-module">
                        <h3 className="sec-module__title">{module.charAt(0).toUpperCase() + module.slice(1)}</h3>
                        <div className="sec-module__list">
                          {perms.map(perm => (
                            <label key={perm} className="sec-check">
                              <input
                                type="checkbox"
                                checked={(permissions[module] || []).includes(perm)}
                                onChange={() => togglePermission(module, perm)}
                              />
                              <span>{perm.replace(/_/g, ' ')}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="sec-actions">
                    <button
                      onClick={handleSavePermissions}
                      className="btn btn--primary"
                      disabled={loading}
                    >
                      <Save aria-hidden="true" /> {t('Save Permissions')}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Security;